/**
 * `useVoiceCapture` — the headless voice recording hook.
 *
 * Owns MediaRecorder + timer + preview blob state. Zero UI opinions:
 * consumers wire it into any presentation layer (the standalone Capture
 * voice button today, the Assistant chat mic tomorrow).
 *
 * Lifecycle:
 *   idle → recording → (stop) → previewing → (upload) → idle
 *   preview.discard() at any point returns to idle.
 *
 * Everything the hook exposes is stable across renders (`useCallback`) so
 * consumers can safely put refs to `start` / `stop` etc in effect deps.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  transcribeVoice,
  type VoiceParseResult,
  type TranscribeVoiceOptions,
} from "./voiceParse";

export type VoiceCaptureStatus =
  | "idle"
  | "recording"
  | "previewing"
  | "uploading";

export interface VoiceCapturePreview {
  blob: Blob;
  url: string;
  /** Duration in whole seconds captured client-side (not from audio metadata). */
  duration: number;
}

export interface UseVoiceCaptureOptions {
  /**
   * MediaRecorder MIME type. Defaults to audio/webm. Kept overridable in
   * case a future Safari path needs audio/mp4. Whisper accepts both.
   */
  mimeType?: string;
}

export interface UseVoiceCaptureReturn {
  status: VoiceCaptureStatus;
  elapsedSec: number;
  liveStream: MediaStream | null;
  preview: VoiceCapturePreview | null;
  /** Upload progress in percent while `status === "uploading"`. */
  uploadPercent: number;
  /** Non-null when the mic denied / MediaRecorder failed. */
  error: string | null;
  start: () => Promise<void>;
  stop: () => void;
  discard: () => void;
  /** Sends the current preview to Whisper. Returns the validated parse. */
  upload: (options?: TranscribeVoiceOptions) => Promise<VoiceParseResult>;
}

export function useVoiceCapture(
  options: UseVoiceCaptureOptions = {}
): UseVoiceCaptureReturn {
  const { mimeType = "audio/webm" } = options;

  const [status, setStatus] = useState<VoiceCaptureStatus>("idle");
  const [elapsedSec, setElapsedSec] = useState(0);
  const [liveStream, setLiveStream] = useState<MediaStream | null>(null);
  const [preview, setPreview] = useState<VoiceCapturePreview | null>(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Recorder + timer refs — kept out of state so an update inside the
  // `ondataavailable` callback doesn't force a re-render every 250ms.
  const recRef = useRef<{
    mr: MediaRecorder | null;
    chunks: BlobPart[];
    stream: MediaStream | null;
  }>({ mr: null, chunks: [], stream: null });
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedRef = useRef(0);
  const previewRef = useRef<VoiceCapturePreview | null>(null);

  useEffect(() => {
    previewRef.current = preview;
  }, [preview]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Cleanup — revoke blob URL + stop tracks on unmount.
  useEffect(
    () => () => {
      clearTimer();
      recRef.current.stream?.getTracks().forEach((t) => t.stop());
      const p = previewRef.current;
      if (p?.url) URL.revokeObjectURL(p.url);
    },
    [clearTimer]
  );

  const stop = useCallback(() => {
    recRef.current.mr?.stop();
  }, []);

  const start = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType });
      recRef.current = { mr, chunks: [], stream };
      setLiveStream(stream);
      mr.ondataavailable = (ev: BlobEvent) => {
        if (ev.data.size > 0) recRef.current.chunks.push(ev.data);
      };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setLiveStream(null);
        clearTimer();
        const blob = new Blob(recRef.current.chunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setPreview((prev) => {
          if (prev?.url) URL.revokeObjectURL(prev.url);
          return { blob, url, duration: elapsedRef.current };
        });
        setStatus("previewing");
      };
      mr.start();
      elapsedRef.current = 0;
      setElapsedSec(0);
      setStatus("recording");
      timerRef.current = setInterval(() => {
        elapsedRef.current += 1;
        setElapsedSec(elapsedRef.current);
      }, 1000);
    } catch (err) {
      setError(
        err instanceof Error && err.name === "NotAllowedError"
          ? "Microphone permission required"
          : "Couldn't start recording"
      );
      setStatus("idle");
    }
  }, [mimeType, clearTimer]);

  const discard = useCallback(() => {
    setPreview((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
    setUploadPercent(0);
    setStatus((s) => (s === "recording" ? s : "idle"));
  }, []);

  const upload = useCallback(
    async (opts: TranscribeVoiceOptions = {}) => {
      if (!preview) throw new Error("Nothing to upload");
      setStatus("uploading");
      setUploadPercent(0);
      try {
        const result = await transcribeVoice(preview.blob, {
          ...opts,
          onProgress: (p) => {
            setUploadPercent(p);
            opts.onProgress?.(p);
          },
        });
        // Success — free the blob URL. Callers get the parse result and
        // navigate away, so we don't need to keep the audio around.
        if (preview.url) URL.revokeObjectURL(preview.url);
        setPreview(null);
        setStatus("idle");
        setUploadPercent(0);
        return result;
      } catch (err) {
        setStatus("previewing");
        setUploadPercent(0);
        throw err;
      }
    },
    [preview]
  );

  return {
    status,
    elapsedSec,
    liveStream,
    preview,
    uploadPercent,
    error,
    start,
    stop,
    discard,
    upload,
  };
}
